import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import SingleInvoice, { PInvoice } from './SingleInvoice';
import { TFunction } from 'i18next';
import { useParameters } from '@web/utils';
import {
  StyledPagination,
  StyledTable,
} from '@/commons/ui/theme/materialStyles';
import { TableNotFound } from '@/components/TableNotFound';
import { useCustomToast } from '@/hooks/useCustomToast';
import { Invoice } from '@/types';
import { useCreatePaymentForInvoiceMutation } from '../../InvoiceDetailsPage/invoice.generated';
import { TableBody, TableCell } from '@mui/material';

// ----------------------------------------------------------------------

type TableKey = keyof Invoice;

const TABLE_HEAD = (
  t: TFunction,
): {
  id: TableKey;
  label: string;
  alignRight: boolean;
}[] => [
  // {
  //   id: 'id',
  //   label: '',
  //   alignRight: false,
  // },
  {
    id: 'id',
    label: t('invoices.table_columns.invoice_number'),
    alignRight: false,
  },
  {
    id: 'totalGross',
    label: t('invoices.table_columns.total_gross'),

    alignRight: false,
  },
  {
    id: 'issuedAt',
    label: t('invoices.table_columns.invoice_date'),

    alignRight: false,
  },
  {
    id: 'status',
    label: t('invoices.table_columns.invoice_status'),
    alignRight: false,
  },
];

// ----------------------------------------------------------------------

interface InvoicesTableProps {
  invoiceList: PInvoice[];
}

interface InvoiceParams {
  retailerId: string;
}

const InvoicesTable: React.FC<InvoicesTableProps> = (props) => {
  const { invoiceList } = props;
  const { retailerId } = useParameters<InvoiceParams>();
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(20);
  const [selected, setSelected] = useState<string[]>([]);
  const navigate = useNavigate();

  const handleSelect = (_event, id: string) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: string[] = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  };

  const handlePageChange = (e, newPage) => {
    if (newPage > page) {
      setPage((val) => val + 1);
    } else {
      setPage((val) => val - 1);
    }
  };

  const handleRowsPerPageChange = (e) => {
    setPerPage(e.target.value);
  };

  const handleRowClick = (invoice: PInvoice) => {
    navigate(`/documents/${retailerId}/invoices/${invoice.id}`);
  };

  const invoices = useMemo(() => {
    const start = page * perPage;
    const end = start + perPage;
    return invoiceList ? invoiceList.slice(start, end) : [];
  }, [invoiceList, page, perPage]);

  const isNotFound = !invoices.length && !!invoices;

  const [_, createPaymentInvoice] = useCreatePaymentForInvoiceMutation();
  const toast = useCustomToast();

  const handleCreateInvoicePayment = async (
    invoiceId: string,
    paymentOptionKey: string,
  ) => {
    try {
      const response = await createPaymentInvoice({
        invoiceId: invoiceId,
        paymentOptionKey: paymentOptionKey,
      });

      if (response.data?.createPaymentForInvoice?.checkoutUrl) {
        window.open(
          response.data?.createPaymentForInvoice?.checkoutUrl,
          '_blank',
        );
      } else {
        toast('error', t('invoices.error_creating_invoice'));
      }
    } catch (error) {
      console.log(error);
      toast('error', t('invoices.error_creating_invoice'));
    }
  };

  return (
    <div className="py-2-0">
      <StyledTable stickyHeader>
        <thead>
          <tr>
            {TABLE_HEAD(t).map((headCell) => (
              <TableCell
                key={headCell.id}
                align={headCell.alignRight ? 'right' : 'left'}
              >
                <span className="text-body1 font-rg opacity-80 capitalize">
                  {headCell.label}
                </span>
              </TableCell>
            ))}
          </tr>
        </thead>
        <TableBody>
          {invoices.map((invoice) => {
            return (
              <SingleInvoice
                key={invoice.id}
                invoice={invoice}
                handleRowClick={handleRowClick}
              />
            );
          })}
          <tr style={{ width: '100%' }}>
            <StyledPagination
              count={invoiceList.length}
              page={page}
              rowsPerPageOptions={[20, 50, 100]}
              rowsPerPage={perPage}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </tr>

          {isNotFound && <TableNotFound />}
        </TableBody>
      </StyledTable>
    </div>
  );
};

export default InvoicesTable;
